import { ReactNode, createContext, useContext, useEffect, useState } from 'react';
import tw from 'twin.macro';

import type { ApolloError } from '@apollo/client';
import * as Sentry from '@sentry/browser';
import { useLocation, useNavigate } from 'react-router-dom';

import { useGetMyProfileQuery } from '@/domains/user';
import { Roles } from '@/libs/graphql';
import NoAccess from '@/routes/no-access';

import { useAuthContext } from './Auth';
import { AUTH_STATE, AUTH_TYPE } from './types';

// Signed in but not whitelisted (no users doc / company). Message check is a fallback for backends
// that don't return the FORBIDDEN code yet.
const isNoAccessError = (error?: ApolloError): boolean =>
  !!error?.graphQLErrors.some(
    (e) => e.extensions?.code === 'FORBIDDEN' || e.message.startsWith('Unauthorized'),
  );

export type User = {
  name: string;
  email: string;
  position?: string;
  company?: string;
  roles?: Roles[];
};

export type BackofficeCtx = {
  authState: AUTH_STATE;
  user?: User | null;
  error?: string;
  loading: boolean;
};

const BackofficeContext = createContext<BackofficeCtx | undefined>(undefined);

const BackofficeProvider = ({ children }: { children?: ReactNode }) => {
  const { authState, authType, authUserData } = useAuthContext();
  const [user, setUser] = useState<User | null>();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    data: myProfileData,
    loading: myProfileLoading,
    error: myProfileError,
  } = useGetMyProfileQuery({
    skip: authState !== AUTH_STATE.LOGGED_IN || authType !== AUTH_TYPE.BACKOFFICE,
  });

  useEffect(() => {
    console.log('checking auth state', authState, authType);
    if (authState === AUTH_STATE.LOGGED_IN) {
      if (authType !== AUTH_TYPE.BACKOFFICE) {
        console.log('not backoffice, logout');
        navigate('/logout');
      }
    } else if (authState === AUTH_STATE.NOT_LOGGED_IN) {
      console.log('not logged in, login');
      navigate(`/backoffice/login?redirect=${encodeURIComponent(location.pathname + location.search)}`);
    }

    return () => {};
  }, [authState, authType]);

  useEffect(() => {
    if (myProfileData) {
      setUser({
        name: myProfileData?.getMyProfile.displayName ?? '',
        email: myProfileData?.getMyProfile.email ?? '',
        roles: myProfileData?.getMyProfile.roles ?? [],
      });
      Sentry.setContext('user', {
        id: myProfileData?.getMyProfile.id,
        name: myProfileData?.getMyProfile.displayName,
        email: myProfileData?.getMyProfile.email,
      });
      setLoading(false);
    } else {
      setUser(null);
    }
  }, [myProfileData]);

  if (isNoAccessError(myProfileError)) {
    return <NoAccess email={authUserData?.email} />;
  }

  if (!user || (user && !user?.roles)) {
    return <div tw="grid h-screen w-screen place-items-center text-2xl">Loading...</div>;
  }

  return (
    <BackofficeContext.Provider
      value={{
        user,
        authState,
        error,
        loading,
      }}
    >
      {children}
    </BackofficeContext.Provider>
  );
};

const useBackofficeContext = (): BackofficeCtx => {
  const context = useContext(BackofficeContext);
  if (context === undefined) {
    throw new Error('useBackofficeContext must be used within BackofficeProvider');
  }
  return context;
};

export { BackofficeContext, useBackofficeContext };
export default BackofficeProvider;
